import styled from "@emotion/styled";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Typography } from "@mui/material";
import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { baseTheme, IconButton, useBreakpoint } from "@avenue-8/sidekick-ui";
import type { PromptLibraryItem } from "@shared/apis/sidekick/generated";

const CardsRow = styled.div`
  position: relative;
  width: 100%;

  .swiper {
    width: 100%;
  }

  .swiper-slide {
    height: auto !important;
    display: flex;
  }
`;

const NavButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: ${(p) => p.theme.colors.background};
  border: solid 1px ${(p) => p.theme.button.outlined.colors.border};

  &:hover,
  &:active,
  &:focus {
    border: solid 1px ${(p) => p.theme.button.outlined.hover.border};
  }
`;

const PrevButton = styled(NavButton)`
  left: -3.25rem;
`;

const NextButton = styled(NavButton)`
  right: -3.25rem;
`;

const Card = styled.button`
  width: 100%;
  height: 100%;
  min-height: 110px;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid ${(p) => p.theme.colors.border2};
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.text.colors.primary};
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.colors.background5};
    border-color: ${(p) => p.theme.colors.border2};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const lineClamp = (lines: number) => `
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  overflow: hidden;
  word-break: break-word;
  min-width: 0;
  width: 100%;
`;

const CardTitle = styled(Typography)`
  color: ${(p) => p.theme.text.colors.brand};
  ${lineClamp(2)}
`;

const CardBody = styled(Typography)`
  color: ${(p) => p.theme.text.colors.primary};
  ${lineClamp(5)}
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  color: ${(p) => p.theme.text.colors.brand};
`;

type Props = {
  items: PromptLibraryItem[];
  disabled?: boolean;
  onSelectItem: (item: PromptLibraryItem) => void;
};

export const CategoryCards = ({ items, disabled, onSelectItem }: Props) => {
  const isLaptopDown = useBreakpoint("laptop");
  const hasNav = !isLaptopDown;
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const updateNavState = (s: SwiperType) => {
    setIsBeginning(s.isBeginning);
    setIsEnd(s.isEnd);
  };

  return (
    <CardsRow>
      {hasNav && !isBeginning && (
        <PrevButton aria-label="Previous" onClick={() => swiper?.slidePrev()}>
          <ChevronLeftIcon fontSize="small" />
        </PrevButton>
      )}
      <Swiper
        spaceBetween={16}
        observer
        observeParents
        resizeObserver
        updateOnWindowResize
        onSwiper={(s) => {
          setSwiper(s);
          updateNavState(s);
        }}
        onSlideChange={updateNavState}
        onReachEnd={() => setIsEnd(true)}
        onReachBeginning={() => setIsBeginning(true)}
        onFromEdge={updateNavState}
        breakpoints={{
          0: { slidesPerView: 1.4 },
          [baseTheme.breakpoints.tablet + 1]: { slidesPerView: 2.4 },
          [baseTheme.breakpoints.laptop + 1]: { slidesPerView: 4 },
          [baseTheme.breakpoints.desktop + 1]: {
            slidesPerView: 5,
          },
        }}
      >
        {items.map((item: PromptLibraryItem) => (
          <SwiperSlide key={item.id}>
            <Card
              type="button"
              disabled={disabled}
              onClick={() => onSelectItem(item)}
            >
              <CardTitle variant="subtitle2">{item.title}</CardTitle>
              <CardBody>{item.displayText}</CardBody>
              <CardFooter>
                <ArrowForwardIcon fontSize="small" />
              </CardFooter>
            </Card>
          </SwiperSlide>
        ))}
      </Swiper>
      {hasNav && !isEnd && (
        <NextButton aria-label="Next" onClick={() => swiper?.slideNext()}>
          <ChevronRightIcon fontSize="small" />
        </NextButton>
      )}
    </CardsRow>
  );
};
